import { lazy, Suspense, type ComponentType } from 'react';
import { FullPageLoader } from '@/shared/components';
import { validateOidcConfig } from './oidc-config';
import {
  EAuthProviderType,
  type IAuthAdapter,
  type IAuthAdapterProviderProps,
  type IAuthProviderConfig,
} from './types';

// Dynamic on purpose: an Auth0-only deployment must not download a second auth
// SDK. `oidc-bundle-isolation.test.ts` holds the line.
const OidcRuntimeProvider = lazy(() => import('./oidc-runtime-provider'));

/**
 * One provider component per key, because `IAuthAdapter.Provider` has to be a
 * stable reference — a fresh identity each render would remount the whole app.
 */
const providerComponentsByKey = new Map<
  string,
  ComponentType<IAuthAdapterProviderProps>
>();

const getProviderComponent = (
  providerKey: string,
): ComponentType<IAuthAdapterProviderProps> => {
  const cached = providerComponentsByKey.get(providerKey);
  if (cached) return cached;

  const OidcAdapterProvider = ({ children }: IAuthAdapterProviderProps) => (
    <Suspense fallback={<FullPageLoader />}>
      <OidcRuntimeProvider providerKey={providerKey}>
        {children}
      </OidcRuntimeProvider>
    </Suspense>
  );
  OidcAdapterProvider.displayName = `OidcAdapterProvider(${providerKey})`;

  providerComponentsByKey.set(providerKey, OidcAdapterProvider);
  return OidcAdapterProvider;
};

/**
 * Builds the adapter for a customer-owned OIDC provider.
 *
 * @param {IAuthProviderConfig} provider - Entry from the runtime `authProviders` list
 * @returns {IAuthAdapter} Adapter backed by `oidc-client-ts`
 */
export const createOidcAdapter = (
  provider: IAuthProviderConfig,
): IAuthAdapter => ({
  key: provider.key,
  type: EAuthProviderType.OIDC,
  Provider: getProviderComponent(provider.key),
  validateConfig: () => validateOidcConfig(provider),
});
